import { makeAutoObservable } from 'mobx'
import { type IActionsProps, type IAvatarInfo, type IIconsVector } from 'shared/ui'
import { isIcon } from 'shared/ui/Icon/icons'
import type { IAreaCode, IContactIntegrationImplement, INote } from 'entities/Contacts'
import {
  type IResponseContact,
  type IResponseCustomField,
  type IResponseNote,
  type IResponseOptInRequests,
  type IResponseShortcodeOptOut,
} from 'entities/Contacts/api/types'
import { Tag } from 'entities/Tags/model/Tag'
import { type IResponseConversationDraftMessageContact } from 'entities/Conversation/api/types'
import { ContactHubspot } from 'entities/Contacts/model/Integrations/ContactHubspot'
import { ContactActivecampaign } from 'entities/Contacts/model/Integrations/ContactActivecampaign'
import { ContactInfusionsoft } from 'entities/Contacts/model/Integrations/ContactInfusionsoft'
import { ContactPipedrive } from 'entities/Contacts/model/Integrations/ContactPipedrive'
import { ContactSalesforce } from 'entities/Contacts/model/Integrations/ContactSalesforce'
import { type ContactIntegrationBase } from 'entities/Contacts/model/Integrations/ContactIntegrationBase'
import { type IIntegrationKey } from 'entities/Integrations'
import { ContactUnified } from 'entities/Contacts/model/Integrations/ContactUnified'

export class Contact {
  id: number
  key: number
  first_name: string
  last_name: string
  full_name: string
  number: string
  formatted_number: string
  photo_url: string
  color: string
  email: string
  timezone?: string

  hubspot: ContactHubspot | null
  activecampaign: ContactActivecampaign | null
  infusionsoft: ContactInfusionsoft | null
  pipedrive: ContactPipedrive | null
  salesforce: ContactSalesforce | null
  zoho: ContactUnified | null

  is_blocked?: boolean
  custom_fields: IResponseCustomField[]
  national_number: string
  conversationId: number | null = null
  notes: INote[]
  tags: Tag[]
  integration_type: 'salesmsg'
  integration_key: IIntegrationKey
  integration_vendor_id: string
  integration_vendor_type: string | null
  created_at: string
  archived_at: string | null
  area_code_info: IAreaCode | null
  updated_at: string
  is_invalid: boolean
  lookup_type: string | null
  carrier_name: string | null
  opt_out: string | null
  opt_out_at: string | null
  opt_out_at_shortcode: string | null
  opt_in_at: string
  opt_in_request_at: string
  shortcode_opt_outs: IResponseShortcodeOptOut[]
  last_messaged_on: string | null
  last_message_sent_on: string | null
  last_message_received_on: string | null
  last_called_on: string | null
  last_contacted_on: string | null
  message_status: string | null
  opt_in: string | null
  keywords: IResponseContact['keywords'] | []
  total_received_sms: string | null
  total_received_call: string | null
  origin: IResponseContact
  source: string
  opt_in_requests: IResponseOptInRequests[] = []
  country_calling_code: string
  owner_id: number | null
  prevent_autolink: boolean
  isDisabled = false
  ariaLabel: string | undefined
  mobile_phone = ''
  actionsProps?: IActionsProps | null = null
  icon?: IIconsVector

  constructor(item: IResponseContact & { icon?: IIconsVector }) {
    this.icon = item.icon
    this.id = item.id
    this.key = item.id
    this.first_name = item.first_name ?? ''
    this.last_name = item.last_name ?? ''
    this.full_name = item.full_name.trim()
    this.photo_url = item.photo_url
    this.color = item.color ? item.color : this.generateColor
    this.number = item.number
    this.formatted_number = item.formatted_number
    this.email = item.email || ''
    this.integration_key = item.integration?.key || 'salesmsg'
    this.integration_type = 'salesmsg'
    this.integration_vendor_id = item.integration_vendor_id
    this.integration_vendor_type = item.integration_vendor_type || null

    this.hubspot = item.hubspot ? new ContactHubspot(item.hubspot) : null
    this.activecampaign = item.activecampaign
      ? new ContactActivecampaign(item.activecampaign)
      : null
    this.infusionsoft = item.infusionsoft ? new ContactInfusionsoft(item.infusionsoft) : null
    this.pipedrive = item.pipedrive ? new ContactPipedrive(item.pipedrive) : null
    this.salesforce = item.salesforce ? new ContactSalesforce(item.salesforce) : null
    this.zoho = item.zoho ? new ContactUnified(item.zoho) : null

    this.national_number = item.national_number
    this.timezone = item.timezone
    this.is_blocked = item.is_blocked
    this.custom_fields = item.custom_fields
    this.conversationId = item.conversationId || item.conversation?.id || null
    this.notes = this.getEnrichNotes(item.notes) || []
    this.tags = Array.isArray(item.tags) ? item.tags.map((item) => new Tag(item)) : []
    this.created_at = item.created_at
    this.archived_at = item.archived_at
    this.area_code_info = item.area_code || null
    this.updated_at = item.updated_at
    this.is_invalid = item.is_invalid
    this.lookup_type = item.lookup_type
    this.carrier_name = item.carrier_name
    this.opt_out = item.opt_out || null
    this.opt_out_at = item.opt_out_at || null
    this.opt_out_at_shortcode = item.opt_out_at_shortcode
    this.opt_in_at = item.opt_in_at || ''
    this.opt_in_request_at = item.opt_in_request_at || ''
    this.shortcode_opt_outs = item.shortcode_opt_outs || []
    this.last_messaged_on = item.last_messaged_on
    this.last_message_sent_on = item.last_message_sent_on
    this.last_message_received_on = item.last_message_received_on
    this.last_called_on = item.last_called_on
    this.last_contacted_on = item.last_contacted_on
    this.message_status = item.message_status
    this.opt_in = item.opt_in
    this.keywords = item.keywords
    this.total_received_sms = item.total_received_sms
    this.total_received_call = item.total_received_call
    this.origin = item
    this.source = item.source
    this.opt_in_requests = item.opt_in_requests || []
    this.country_calling_code = item.country_calling_code || ''
    this.owner_id = item.owner_id || null
    this.prevent_autolink = item.prevent_autolink || false
    this.isDisabled = !!item.disabled

    if (this.integrationInfo.integration_type === 'pipedrive') {
      this.national_number = this.integrationInfo.phone
      this.email = this.integrationInfo.email
      this.first_name = this.integrationInfo.first_name ?? ''
      this.last_name = this.integrationInfo.last_name ?? ''
      this.full_name = this.integrationInfo.full_name
    }

    makeAutoObservable(this)
  }

  get integrationIcon() {
    if (isIcon(this.integration_key)) {
      return this.integration_key
    }

    return 'salesmsg'
  }

  get localIcon() {
    return this.integration_type
  }

  get area_code() {
    return this.area_code_info?.area_code || ''
  }

  get country() {
    return this.area_code_info?.country_code || ''
  }

  get countryCode() {
    return this.area_code_info?.country_code
  }

  get state_code() {
    return this.area_code_info?.state_code || ''
  }

  get state_name() {
    return this.area_code_info?.state_name || ''
  }

  get time_zone() {
    return this.area_code_info?.time_zone || ''
  }

  get avatarInfo(): IAvatarInfo {
    const isPhoneAbbr = !this.first_name && !this.last_name

    return {
      isPhoneAbbr,
      firstName: this.first_name,
      lastName: this.last_name,
      image: this.photo_url,
      color: this.color,
      number: this.number,
      icon: this.icon,
    }
  }

  get isUnknownContact() {
    return !this.first_name.trim() && !this.last_name.trim()
  }

  get customFields() {
    return this.custom_fields || []
  }

  get hasNotes() {
    return !!this.notes.length
  }

  get notesCount() {
    return this.hasNotes ? this.notes.length : 0
  }

  get sortedNotes() {
    return this.notes.slice().sort(function (a: INote, b: INote) {
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    })
  }

  get hasMoreNotes() {
    return this.notesCount > 3
  }

  get last3Notes() {
    if (this.notesCount < 4) return this.sortedNotes
    return this.sortedNotes.slice(0, 3)
  }

  get name() {
    let output = ''
    const full_name = this.full_name.trim()

    if (full_name) {
      output = full_name
    } else if (this.email) {
      output = this.email
    } else if (this.number) {
      output = this.number
    }

    return output ? output : ''
  }

  get phoneInfo() {
    return this.formatted_number || this.national_number || this.number || ''
  }

  get defaultPhoneInfo() {
    return this.formatted_number || this.national_number || this.number
  }

  get integrationInfo(): Omit<
    ContactIntegrationBase<IContactIntegrationImplement>,
    'origin' | 'integration_vendor_id' | 'params'
  > {
    if (this.hubspot) return this.hubspot
    if (this.activecampaign) return this.activecampaign
    if (this.infusionsoft) return this.infusionsoft
    if (this.pipedrive) return this.pipedrive
    if (this.salesforce) return this.salesforce
    if (this.zoho) return this.zoho

    return {
      id: this.id,
      key: this.id,
      email: this.email,
      first_name: this.first_name,
      last_name: this.last_name,
      full_name: this.full_name,
      national_number: this.national_number,
      phone: this.national_number,
      mobile_phone: '',
      custom_phone: '',
      photo_url: this.photo_url,
      integration_type: this.integration_type,
      color: this.color,
      avatarInfo: this.avatarInfo,
      integrationIcon: this.integrationIcon,
      localIcon: this.localIcon,
      number: this.number,
      formatted_number: this.formatted_number,
      phoneCount: this.phoneCount,
      phoneInfo: this.phoneInfo,
      name: this.name,
      integration_key: this.integration_key,
      phoneNumbers: [],
      defaultPhoneInfo: this.defaultPhoneInfo,
    }
  }

  get phoneCount() {
    return 0
  }

  get generateColor() {
    const colors = ['#6372A5', '#1D95F2', '#10D0AD', '#E13466', '#FFC422', '#FF9423', '#A233E1']

    return colors[+this.id % colors.length]
  }

  get optInLocal(): IResponseShortcodeOptOut {
    return {
      id: 'local',
      disabled: this.opt_out === 'hard',
      name: 'Local and Toll-Free numbers',
      value: this.opt_out === null,
      number_id: 0,
      timestamp: null,
      formatted_number: '',
    }
  }

  get optInShortCode(): IResponseShortcodeOptOut[] {
    if (!Array.isArray(this.shortcode_opt_outs)) return []

    return this.shortcode_opt_outs
  }

  get optItems() {
    return [this.optInLocal, ...this.optInShortCode]
  }

  get isOptOut() {
    return !this.optInLocal.value
  }

  get isBlocked() {
    return !!this.is_blocked
  }

  get isSyncedWithHubspot() {
    return !!this.hubspot || this.integration_key === 'hubspot'
  }

  get isOptOutHard() {
    return this.opt_out === 'hard'
  }

  get isNew() {
    return this.id === -1 || this.id === 0
  }

  get isArchived() {
    return Boolean(this.archived_at)
  }

  get isOptedIn() {
    return this.opt_in_at && (this.opt_in_request_at || this.opt_in_requests.length > 0)
  }

  getEnrichNotes = (notes?: IResponseNote[]) =>
    notes?.map((note) => {
      const { created_by } = note

      if (!created_by) return { ...note, avatarInfo: {} }

      return {
        ...note,
        avatarInfo: {
          id: created_by?.id,
          firstName: created_by?.first_name,
          lastName: created_by?.last_name,
          image: created_by?.photo_url,
          color: created_by?.color,
        },
      }
    }) ?? []

  handleUpdateNotes = (items: INote[] = [], responseNotes?: IResponseNote[]) => {
    if (responseNotes) {
      items.push(...this.getEnrichNotes(responseNotes))
    }

    this.notes = items
  }

  handleUpdateTags = (items: Tag[]) => {
    this.tags = items
  }

  getReceivedOptInRequestFromNumber(numberId?: number) {
    if (!numberId) return false

    return this.opt_in_requests.some((oir) => numberId === oir.number_id)
  }

  syncOrigin = (origin: IResponseContact) => {
    this.id = origin.id
    this.first_name = origin.first_name ?? ''
    this.last_name = origin.last_name ?? ''
    this.full_name = origin.full_name.trim()
    this.photo_url = origin.photo_url
    this.color = origin.color ? origin.color : this.generateColor
    this.number = origin.number
    this.formatted_number = origin.formatted_number
    this.email = origin.email || ''
    this.integration_vendor_id = origin.integration_vendor_id
    this.integration_vendor_type = origin.integration_vendor_type || null

    this.hubspot = origin.hubspot ? new ContactHubspot(origin.hubspot) : null
    this.activecampaign = origin.activecampaign
      ? new ContactActivecampaign(origin.activecampaign)
      : null
    this.infusionsoft = origin.infusionsoft ? new ContactInfusionsoft(origin.infusionsoft) : null
    this.pipedrive = origin.pipedrive ? new ContactPipedrive(origin.pipedrive) : null
    this.salesforce = origin.salesforce ? new ContactSalesforce(origin.salesforce) : null
    this.zoho = origin.zoho ? new ContactUnified(origin.zoho) : null

    this.national_number = origin.national_number
    this.timezone = origin.timezone
    this.is_blocked = origin?.is_blocked || false
    this.custom_fields = origin.custom_fields || []
    this.conversationId = origin.conversationId || origin.conversation?.id || null
    this.notes = this.getEnrichNotes(origin.notes)
    this.tags = Array.isArray(origin.tags) ? origin.tags.map((item) => new Tag(item)) : []
    this.integration_key = origin.integration?.key
    this.created_at = origin.created_at
    this.area_code_info = origin.area_code || null
    this.updated_at = origin.updated_at
    this.is_invalid = origin.is_invalid
    this.lookup_type = origin.lookup_type
    this.carrier_name = origin.carrier_name
    this.opt_out = origin.opt_out || null
    this.opt_out_at = origin.opt_out_at || null
    this.opt_out_at_shortcode = origin.opt_out_at_shortcode
    this.opt_in_at = origin.opt_in_at || ''
    this.opt_in_request_at = origin.opt_in_request_at || ''
    this.shortcode_opt_outs = origin.shortcode_opt_outs || []
    this.last_messaged_on = origin.last_messaged_on
    this.last_message_sent_on = origin.last_message_sent_on
    this.last_message_received_on = origin.last_message_received_on
    this.last_called_on = origin.last_called_on
    this.last_contacted_on = origin.last_contacted_on
    this.message_status = origin.message_status
    this.opt_in = origin.opt_in
    this.keywords = origin.keywords
    this.total_received_sms = origin.total_received_sms
    this.total_received_call = origin.total_received_call
    this.origin = origin
    this.source = origin.source
    this.opt_in_requests = origin.opt_in_requests || []
    this.country_calling_code = origin.country_calling_code || ''
    this.owner_id = origin.owner_id || null
    this.prevent_autolink = origin.prevent_autolink || false

    if (this.integrationInfo.integration_type === 'pipedrive') {
      this.national_number = this.integrationInfo.phone
      this.email = this.integrationInfo.email
      this.first_name = this.integrationInfo.first_name ?? ''
      this.last_name = this.integrationInfo.last_name ?? ''
      this.full_name = this.integrationInfo.full_name
    }
  }

  get dataDraft(): IResponseConversationDraftMessageContact {
    return {
      color: this.color,
      country: this.country,
      country_calling_code: this.country_calling_code,
      created_at: this.created_at,
      custom_fields: this.custom_fields,
      email: this.email,
      first_name: this.first_name,
      formatted_number: this.formatted_number,
      full_name: this.full_name,
      id: this.id,
      integration_vendor_id: this.integration_vendor_id,
      integration_vendor_type: this.integration_vendor_type,
      last_name: this.last_name,
      national_number: this.national_number,
      number: this.number,
      opt_in_at: this.opt_in_at,
      opt_in_request_at: this.opt_in_request_at,
      opt_in_requests: this.opt_in_requests,
      opt_out: this.opt_out,
      opt_out_at: this.opt_out_at,
      owner_id: this.owner_id,
      photo_url: this.photo_url,
      prevent_autolink: this.prevent_autolink,
      tags: this.tags,
      updated_at: this.updated_at,
    }
  }
}
